/* eslint-disable no-restricted-syntax */
import { defineStore } from 'pinia';
import { useSessionStorage, useStorage } from '@vueuse/core';
import authService from '@/services/authService';
import { computed } from 'vue';
// eslint-disable-next-line import/no-cycle
import useErrors from '@/hooks/useErrors';

export default defineStore('authStore', () => {
  const session = useSessionStorage('auth-session', {
    active: false,
    firstName: null,
    lastName: null,
    sid: null,
    st: null,
    scope: [],
    role: null,
    institution: null,
    secondsToLive: null,
    secondsToCountdown: null,
  });

  const returnUrl = useSessionStorage('returnPath', '/dashboard');
  const returnPath = useStorage('returnPath', null, sessionStorage);

  const errors = useErrors();
  const isAuthorized = computed(() => session.value.st === 'authorized');
  const isReqRole = computed(() => session.value.st === 'req_role');
  const userDisplayName = computed(() => `${session.value.firstName} ${session.value.lastName}`);
  const organization = computed(() => session.value.institution);
  const roles = computed(() => (session.value.role ? [session.value.role] : []));
  const isAdmin = computed(() => session.value?.role?.code === 'ADMIN');
  const isLNKC = computed(() => session.value?.role?.code === 'LNKC');
  const isCollectiveManager = computed(() => session.value?.role?.code === 'VADIT');
  const isMunicipalCoordinator = computed(() => session.value?.role?.code === 'KOORD');
  const isCollectiveManagerOrMunicipalCoordinator = computed(() =>
    ['KOORD', 'VADIT'].includes(session.value?.role?.code)
  );
  const showNavigation = computed(
    () => session.value.st === 'authorized' && session.value.st !== 'req_agreement'
  );

  async function clearReturnPath() {
    returnPath.value = null;
  }
  async function getReturnPath() {
    return returnPath.value;
  }

  function $reset() {
    session.value = {
      active: false,
      firstName: null,
      lastName: null,
      sid: null,
      st: null,
      scope: [],
      role: null,
      institution: null,
      secondsToLive: null,
      secondsToCountdown: null,
    };
    returnUrl.value = '/dashboard';
    clearReturnPath();
  }

  function fillSession(resp) {
    session.value.sid = resp.sid;
    session.value.active = resp.active;
    session.value.st = resp.st;
    session.value.code = resp.code;
    session.value.firstName = resp.firstName;
    session.value.lastName = resp.lastName;
    session.value.role = resp.role;
    session.value.scope = resp.scope;
    session.value.secondsToLive = resp.secondsToLive;
    session.value.secondsToCountdown = resp.secondsToCountdown;
  }

  async function fetchSession() {
    try {
      const resp = await authService.session();
      if (resp.status === 200) {
        fillSession(resp.data);
      }
    } catch (err) {
      const error = errors.get(err);

      $reset();
      if (error.status !== 401) {
        throw err;
      }
    }
    return session.value;
  }

  async function logout() {
    try {
      return await authService.logout();
    } finally {
      [$reset, sessionStorage.clear].forEach((f) => {
        try {
          f();
        } catch {
          // try next function even if one fails
        }
      });
    }
  }

  async function setRole(role) {
    try {
      const result = await authService.selectRole(role);

      if (result.status === 200) {
        await fetchSession();
      }
    } catch (err) {
      const error = errors.get(err);
      if (error.status === 304) {
        await fetchSession();
      }
      // if (error && error.status === 422 && error.data && error.data.code) {
      //   return;
      // }
      errors.pushWithNotification(err);
    }
  }

  async function keepAlive() {
    try {
      const resp = await authService.keepAlive();
      if (resp.status === 200) {
        fillSession(resp.data);
      }
      return resp;
    } catch (err) {
      $reset();
      throw err;
    }
  }

  async function login(retPath = null) {
    if (retPath) {
      returnPath.value = retPath;
    }
    return authService.authorize();
  }

  // Exporting the store interface
  return {
    session,
    returnUrl,
    isAuthorized,
    isReqRole,
    userDisplayName,
    organization,
    roles,
    isAdmin,
    isLNKC,
    isCollectiveManager,
    isMunicipalCoordinator,
    isCollectiveManagerOrMunicipalCoordinator,
    showNavigation,
    fetchSession,
    logout,
    keepAlive,
    clearReturnPath,
    getReturnPath,
    setRole,
    login,
    $reset,
  };
});
